import {Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';

@Component({
  selector: 'app-barcode-scanner-dialog',
  templateUrl: './barcode-scanner-dialog.component.html',
  styleUrls: ['./barcode-scanner-dialog.component.scss'],
})
export class BarcodeScannerDialogComponent implements OnInit, OnDestroy {
  @ViewChild('videoEl', {static: false}) videoRef: ElementRef<HTMLVideoElement>;
  @ViewChild('canvasEl', {static: false}) canvasRef: ElementRef<HTMLCanvasElement>;

  cameraActive = false;
  cameraError = '';
  manualCode = '';
  scanning = false;
  lastScanned = '';

  private stream: MediaStream;
  private animFrameId: number;
  private detector: any; // BarcodeDetector

  constructor(
    public dialogRef: MatDialogRef<BarcodeScannerDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) {}

  ngOnInit(): void {
    this.initCamera();
  }

  ngOnDestroy(): void {
    this.stopCamera();
  }

  private async initCamera(): Promise<void> {
    // Check if BarcodeDetector is available (Chromium browsers)
    if ('BarcodeDetector' in window) {
      try {
        this.detector = new (window as any).BarcodeDetector({
          formats: ['qr_code', 'ean_13', 'ean_8', 'code_128', 'code_39', 'upc_a', 'upc_e', 'itf', 'codabar'],
        });
      } catch (e) {
        this.detector = null;
      }
    }

    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        video: {facingMode: 'environment', width: {ideal: 1280}, height: {ideal: 720}},
        audio: false,
      });
      this.cameraActive = true;

      // Wait for view to render
      setTimeout(() => {
        if (this.videoRef?.nativeElement) {
          const video = this.videoRef.nativeElement;
          video.srcObject = this.stream;
          video.play();
          this.startScanning();
        }
      }, 100);
    } catch (err) {
      this.cameraError = 'Camera access denied or unavailable. Use manual entry below.';
      this.cameraActive = false;
    }
  }

  private startScanning(): void {
    if (!this.detector || !this.videoRef?.nativeElement) { return; }
    this.scanning = true;
    const video = this.videoRef.nativeElement;

    const scan = async () => {
      if (!this.scanning || video.readyState < 2) {
        this.animFrameId = requestAnimationFrame(scan);
        return;
      }
      try {
        const barcodes = await this.detector.detect(video);
        if (barcodes.length > 0) {
          const code = barcodes[0].rawValue;
          if (code && code !== this.lastScanned) {
            this.lastScanned = code;
            this.onCodeDetected(code);
            return; // stop scanning after detection
          }
        }
      } catch (e) {
        // detection error, continue
      }
      this.animFrameId = requestAnimationFrame(scan);
    };

    this.animFrameId = requestAnimationFrame(scan);
  }

  private onCodeDetected(code: string): void {
    this.scanning = false;
    this.stopCamera();
    this.dialogRef.close({code});
  }

  submitManual(): void {
    if (this.manualCode?.trim()) {
      this.stopCamera();
      this.dialogRef.close({code: this.manualCode.trim()});
    }
  }

  cancel(): void {
    this.stopCamera();
    this.dialogRef.close(null);
  }

  private stopCamera(): void {
    this.scanning = false;
    if (this.animFrameId) {
      cancelAnimationFrame(this.animFrameId);
    }
    if (this.stream) {
      this.stream.getTracks().forEach(t => t.stop());
      this.stream = null;
    }
  }
}
